<script setup>
import { computed, onMounted, onUnmounted } from 'vue'
import {
  BadgePercent,
  CalendarCheck,
  CircleCheck,
  CreditCard,
  Globe,
  Hash,
  Layers,
  RefreshCw,
  Repeat,
  Tag,
} from 'lucide-vue-next'

const props = defineProps({
  // A retailer/offer row: { id, retailerName, retailerWebsite, logo, banner, description,
  // offerCount, highestCashbackValue, highestCashbackType, maxCashback, offerTypes, usageTypes }
  offer: { type: Object, required: true },
  // Site option (featureFlags.showOfferBanners): use the offer's banner as the hero image.
  showBanner: { type: Boolean, default: false },
  // Theme-editor inspect mode: clicking the offer header emits `inspect` instead of acting.
  inspect: { type: Boolean, default: false },
  // Per-component style overrides for the offer header (from regionStyle('offerHeader')).
  headerStyle: { type: Object, default: () => ({}) },
  // Per-component overrides for the content sections, keyed by region
  // (ctaBanner / sectionTitle / ctaTiles).
  sectionStyles: { type: Object, default: () => ({}) },
})
const emit = defineEmits(['close', 'inspect'])

function onRegionClick(e, region) {
  if (!props.inspect) return
  e.preventDefault()
  e.stopPropagation()
  emit('inspect', region)
}

// The cashback pill sits on a light tint of the brand colour, so its text must stay dark even on
// dark palettes. A component-level text colour (theme editor) still wins.
const heroStyle = computed(() => {
  const hs = props.headerStyle || {}
  const pillText = hs['--brand-text']
  return pillText ? { ...hs, '--wl-offer-pill-text': pillText } : hs
})

const ctaBanner = computed(() => (props.offer.ctaBanner?.image ? props.offer.ctaBanner : null))
const ctaTiles = computed(() => (props.offer.ctaTiles || []).filter((t) => t && t.image))
const hasSectionTitle = computed(
  () => !!(props.offer.sectionTitle || props.offer.sectionDescription)
)
function safeLink(url) {
  if (!url) return null
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

const videoCta = computed(() => (props.offer.videoCta?.videoUrl ? props.offer.videoCta : null))
const hasVideoTitle = computed(
  () => !!(props.offer.videoSectionTitle || props.offer.videoSectionDescription)
)
// YouTube / Vimeo page links become embeds; anything else is treated as a direct video file.
const videoEmbed = computed(() => {
  const url = videoCta.value?.videoUrl || ''
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/)
  if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}`
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/)
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`
  return null
})
const termsParagraphs = computed(() =>
  String(props.offer.termsAndConditions || '')
    .split(/\n\s*\n/)
    .map((t) => t.trim())
    .filter(Boolean)
)

const monogram = computed(() => (props.offer.retailerName || '?').charAt(0).toUpperCase())
const hasBanner = computed(() => !!(props.showBanner && props.offer.banner))

const cashbackLabel = computed(() => {
  const v = props.offer.highestCashbackValue
  if (v == null) return ''
  const type = String(props.offer.highestCashbackType || '')
  return type.includes('PERCENTAGE') ? `Earn ${v}% cashback` : `Earn £${v} cashback`
})

const USAGE_LABELS = {
  MULTI_USE: 'Multi-use',
  SINGLE_USE: 'Single use',
  LIMITED_USE: 'Limited use',
}
const types = computed(() => props.offer.offerTypes || [])
const onlineOnly = computed(() => types.value.length === 1 && types.value[0] === 'ONLINE')

const clickToActivate = computed(() => types.value.includes('CLICK_TO_ACTIVATE'))
const cardLinked = computed(
  () => types.value.includes('PAYMENT') || types.value.includes('CLICK_TO_ACTIVATE')
)

// "How it works": the numbered steps a shopper follows for this kind of offer.
const steps = computed(() => {
  const name = props.offer.retailerName
  const out = []
  if (clickToActivate.value) out.push('Activate the offer')
  if (cardLinked.value) out.push(`Pay using your linked card at ${name}`)
  else out.push(`Tap Visit website and shop at ${name}`)
  out.push('Earn cashback')
  return out
})

// "Offer details": one line per thing worth knowing before clicking through, each with an icon.
const details = computed(() => {
  const out = []
  const v = props.offer.highestCashbackValue
  const cap = props.offer.maxCashback
  if (v != null) {
    const rate = String(props.offer.highestCashbackType || '').includes('PERCENTAGE')
      ? `${v}%`
      : `£${v}`
    out.push({
      icon: BadgePercent,
      text: `Up to ${rate} cashback per purchase${cap > 0 ? ` (max £${formatMoney(cap)})` : ''}`,
    })
  }
  if (onlineOnly.value) out.push({ icon: Globe, text: 'Applies to online purchases' })
  else if (cardLinked.value)
    out.push({ icon: CreditCard, text: 'Pay in store or online with your linked card' })
  const usage = [...new Set(props.offer.usageTypes || [])]
  const USAGE_ICONS = { MULTI_USE: Repeat, SINGLE_USE: CircleCheck, LIMITED_USE: Hash }
  usage.forEach((u) =>
    out.push({ icon: USAGE_ICONS[u] || Repeat, text: USAGE_LABELS[u] || titleCase(u) })
  )
  if (clickToActivate.value)
    out.push({ icon: RefreshCw, text: 'Reactivate after it expires to keep using this offer' })
  else out.push({ icon: CalendarCheck, text: 'Always on' })
  const cat = (props.offer.categories || [])[0]
  if (cat) out.push({ icon: Tag, text: `Category: ${titleCase(cat)}` })
  const n = props.offer.offerCount
  if (n > 1) out.push({ icon: Layers, text: `${n} offers available` })
  return out
})

const description = computed(
  () =>
    props.offer.description ||
    (onlineOnly.value
      ? `Shop online with ${props.offer.retailerName} through this site to earn your cashback.`
      : `Earn cashback every time you shop with ${props.offer.retailerName}.`)
)

const website = computed(() => {
  const url = props.offer.retailerWebsite
  if (!url) return null
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
})

function formatMoney(v) {
  return Number.isInteger(v) ? String(v) : v.toFixed(2)
}
function titleCase(v) {
  return String(v)
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function onKeydown(e) {
  if (e.key === 'Escape') emit('close')
}
onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => window.removeEventListener('keydown', onKeydown))
</script>

<template>
  <section class="wl-brand-page">
    <div class="wl-container">
      <button class="wl-back" type="button" @click="$emit('close')">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="m15 18-6-6 6-6" />
        </svg>
        View all offers
      </button>

      <!-- Hero: banner (or brand-coloured fallback), logo tile bottom-left, cashback pill bottom-right -->
      <div
        class="wl-brand-hero"
        :class="{ 'has-banner': hasBanner, 'wl-inspectable': inspect }"
        :style="heroStyle"
        @click.capture="onRegionClick($event, 'offerHeader')"
      >
        <img
          v-if="hasBanner"
          class="wl-brand-banner"
          :src="offer.banner"
          :alt="`${offer.retailerName} offer`"
        />
        <div class="wl-brand-hero-shade" />
        <div class="wl-brand-logo">
          <img v-if="offer.logo" :src="offer.logo" :alt="offer.retailerName" />
          <span v-else class="wl-brand-monogram">{{ monogram }}</span>
        </div>
        <div v-if="cashbackLabel" class="wl-brand-pill">{{ cashbackLabel }}</div>
      </div>

      <div class="wl-brand-body">
        <div class="wl-brand-copy">
          <h1>{{ offer.retailerName }}</h1>
          <p class="wl-brand-description">{{ description }}</p>
          <a
            v-if="website"
            class="wl-brand-cta"
            :href="website"
            target="_blank"
            rel="noopener noreferrer"
          >
            Visit website
          </a>
        </div>

        <!-- Wrapper div: the grid would stretch a direct child to the copy's height. -->
        <div class="wl-brand-side">
          <section class="wl-brand-card">
            <h3>How it works</h3>
            <ol class="wl-brand-steps">
              <li v-for="(step, i) in steps" :key="step">
                <span class="wl-brand-step-num">{{ i + 1 }}</span>
                {{ step }}
              </li>
            </ol>
          </section>
          <section v-if="details.length" class="wl-brand-card">
            <h3>Offer details</h3>
            <ul class="wl-brand-details">
              <li v-for="d in details" :key="d.text">
                <span class="wl-brand-icon"
                  ><component :is="d.icon" :size="18" :stroke-width="1.75"
                /></span>
                {{ d.text }}
              </li>
            </ul>
          </section>
        </div>
      </div>

      <!-- CTA banner -->
      <div
        v-if="ctaBanner"
        class="wl-cta-banner"
        :class="{ 'wl-inspectable': inspect }"
        :style="sectionStyles.ctaBanner"
        @click.capture="onRegionClick($event, 'ctaBanner')"
      >
        <img :src="ctaBanner.image" alt="" loading="lazy" />
        <div class="wl-cta-shade" />
        <a
          v-if="safeLink(ctaBanner.link)"
          class="wl-cta-button"
          :href="safeLink(ctaBanner.link)"
          target="_blank"
          rel="noopener noreferrer"
          >{{ ctaBanner.buttonText || 'Learn more' }}</a
        >
      </div>

      <!-- Section title -->
      <div
        v-if="hasSectionTitle"
        class="wl-section-title"
        :class="{ 'wl-inspectable': inspect }"
        :style="sectionStyles.sectionTitle"
        @click.capture="onRegionClick($event, 'sectionTitle')"
      >
        <h2 v-if="offer.sectionTitle">{{ offer.sectionTitle }}</h2>
        <p v-if="offer.sectionDescription">{{ offer.sectionDescription }}</p>
      </div>

      <!-- CTA tiles -->
      <div
        v-if="ctaTiles.length"
        class="wl-cta-tiles"
        :class="{ 'wl-inspectable': inspect }"
        :style="sectionStyles.ctaTiles"
        @click.capture="onRegionClick($event, 'ctaTiles')"
      >
        <div v-for="(tile, i) in ctaTiles" :key="i" class="wl-cta-tile">
          <div class="wl-cta-tile-media">
            <img :src="tile.image" alt="" loading="lazy" />
            <div class="wl-cta-shade" />
            <a
              v-if="safeLink(tile.link)"
              class="wl-cta-button small"
              :href="safeLink(tile.link)"
              target="_blank"
              rel="noopener noreferrer"
              >{{ tile.buttonText || 'Learn more' }}</a
            >
          </div>
          <p v-if="tile.description" class="wl-cta-tile-copy">{{ tile.description }}</p>
        </div>
      </div>

      <!-- Video section title (same component as the tiles' section title) -->
      <div
        v-if="hasVideoTitle"
        class="wl-section-title"
        :class="{ 'wl-inspectable': inspect }"
        :style="sectionStyles.sectionTitle"
        @click.capture="onRegionClick($event, 'sectionTitle')"
      >
        <h2 v-if="offer.videoSectionTitle">{{ offer.videoSectionTitle }}</h2>
        <p v-if="offer.videoSectionDescription">{{ offer.videoSectionDescription }}</p>
      </div>

      <!-- Video CTA -->
      <div
        v-if="videoCta"
        class="wl-video-cta"
        :class="{ 'wl-inspectable': inspect }"
        :style="sectionStyles.videoCta"
        @click.capture="onRegionClick($event, 'videoCta')"
      >
        <div class="wl-video-frame">
          <iframe
            v-if="videoEmbed"
            :src="videoEmbed"
            title="Video"
            allow="
              accelerometer;
              autoplay;
              clipboard-write;
              encrypted-media;
              gyroscope;
              picture-in-picture;
            "
            allowfullscreen
            loading="lazy"
            referrerpolicy="strict-origin-when-cross-origin"
          />
          <video
            v-else
            :src="videoCta.videoUrl"
            :poster="videoCta.posterImage || undefined"
            controls
            playsinline
            preload="metadata"
          />
        </div>
        <a
          v-if="safeLink(videoCta.link)"
          class="wl-video-button"
          :href="safeLink(videoCta.link)"
          target="_blank"
          rel="noopener noreferrer"
          >{{ videoCta.buttonText || 'Learn more' }}</a
        >
      </div>

      <!-- Offer terms -->
      <div
        v-if="termsParagraphs.length"
        class="wl-offer-terms"
        :class="{ 'wl-inspectable': inspect }"
        :style="sectionStyles.offerTerms"
        @click.capture="onRegionClick($event, 'offerTerms')"
      >
        <h2>Offer terms</h2>
        <p v-for="(para, i) in termsParagraphs" :key="i">{{ para }}</p>
      </div>
    </div>
  </section>
</template>

<style scoped>
.wl-brand-page {
  padding: 24px 0 64px;
}
/* Scoped styles don't inherit the preview's .wl-container rules - re-declare them here
   so the brand page aligns with the rest of the storefront. */
.wl-brand-page .wl-container {
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 20px;
}
.wl-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 20px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--brand-muted, #6b7280);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
}
.wl-back:hover {
  color: var(--brand-text, #1f2937);
}
.wl-back svg {
  width: 16px;
  height: 16px;
}

/* ── Hero ─────────────────────────────────────────────────────────── */
.wl-brand-hero {
  border: 1px solid var(--brand-border, transparent);
  box-shadow: var(--brand-shadow, none);
  position: relative;
  aspect-ratio: 16 / 5;
  min-height: 220px;
  border-radius: calc(var(--brand-radius, 14px) * 1.5);
  overflow: hidden;
  /* No banner: a plain dark blue-grey, deepening to the bottom (never the primary colour). */
  background: linear-gradient(180deg, #475569 0%, #1e293b 100%);
}
.wl-brand-banner {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.wl-brand-hero-shade {
  position: absolute;
  inset: 0;
  /* Same wash as the homepage hero banner: near-clear at the top, deepening to the bottom
     (6% → 28% → 72%). Colour/strength/darken/direction come from the theme's Image overlay
     settings; the default colour is a neutral dark blue-grey, never the brand primary. */
  background: linear-gradient(
    var(--brand-overlay-direction, to bottom),
    color-mix(
        in srgb,
        var(--brand-overlay-colour, #1e293b) calc(6% * var(--brand-overlay-strength, 1)),
        transparent
      )
      0%,
    color-mix(
        in srgb,
        var(--brand-overlay-colour, #1e293b) calc(28% * var(--brand-overlay-strength, 1)),
        transparent
      )
      45%,
    color-mix(
        in srgb,
        var(--brand-overlay-colour, #1e293b) calc(72% * var(--brand-overlay-darken, 1)),
        transparent
      )
      100%
  );
}
.wl-brand-hero.has-banner .wl-brand-banner {
  filter: saturate(0.9);
}
.wl-inspectable {
  cursor: pointer;
}
.wl-inspectable:hover {
  outline: 3px solid #d946ef;
  outline-offset: -3px;
}
.wl-brand-logo {
  position: absolute;
  left: 28px;
  bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 84px;
  height: 84px;
  border-radius: calc(var(--brand-radius, 14px) * 0.9);
  background: #fff;
  overflow: hidden;
  box-shadow: 0 8px 24px -8px rgba(0, 0, 0, 0.5);
}
.wl-brand-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
  object-fit: contain;
}
.wl-brand-monogram {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: var(--brand-primary, #6b7280);
  color: var(--brand-button-text, #fff);
  font-weight: 800;
  font-size: 2rem;
}
.wl-brand-pill {
  position: absolute;
  right: 28px;
  bottom: 24px;
  padding: 16px 32px;
  border-radius: calc(var(--brand-radius, 14px) * 1.2);
  background: color-mix(in srgb, var(--brand-primary, #6b7280) 12%, #fff);
  color: var(--wl-offer-pill-text, color-mix(in srgb, var(--brand-primary, #6b7280) 70%, #000));
  font-weight: 600;
  font-size: clamp(1.1rem, 2.4vw, 1.6rem);
  box-shadow: 0 8px 24px -8px rgba(0, 0, 0, 0.5);
}

/* ── Body ─────────────────────────────────────────────────────────── */
.wl-brand-body {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(280px, 1fr);
  gap: 40px;
  margin-top: 36px;
  padding: 0 28px;
}
.wl-brand-copy h1 {
  margin: 0 0 14px;
  font-family: var(--brand-heading-font, var(--brand-font, inherit));
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--brand-text, #1f2937);
}
.wl-brand-description {
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.6;
  color: var(--brand-text, #1f2937);
  white-space: pre-line;
}
.wl-brand-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 28px;
  min-width: 220px;
  padding: 16px 40px;
  border-radius: calc(var(--brand-radius, 14px) * 2.5);
  background: var(--brand-button, #111827);
  color: var(--brand-button-text, #fff);
  font-weight: 700;
  font-size: 1.1rem;
  text-decoration: none;
}
.wl-brand-cta:hover {
  filter: brightness(1.08);
}
.wl-brand-side {
  display: grid;
  gap: 20px;
  align-self: start; /* cards size to their content, not the copy column */
  align-content: start;
}
.wl-brand-card {
  border: 1px solid var(--brand-border, transparent);
  box-shadow: var(--brand-shadow, none);
  padding: 22px 24px;
  border-radius: calc(var(--brand-radius, 14px) * 1.2);
  /* Same soft tint as the offer cards so it reads as a panel on any palette. */
  background: color-mix(in srgb, var(--brand-text, #1f2937) 4%, var(--brand-bg, #fff));
  color: var(--brand-text, #1f2937);
}
.wl-brand-card h3 {
  margin: 0 0 16px;
  font-family: var(--brand-heading-font, var(--brand-font, inherit));
  font-size: 1rem;
  font-weight: 700;
}
.wl-brand-steps,
.wl-brand-details {
  list-style: none;
  margin: 0;
  padding: 0;
  font-size: 0.95rem;
}
.wl-brand-steps li,
.wl-brand-details li {
  display: flex;
  align-items: center;
  gap: 14px;
}
.wl-brand-steps li + li,
.wl-brand-details li + li {
  margin-top: 14px;
}
.wl-brand-step-num {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  background: var(--brand-primary, #6b7280);
  color: var(--brand-button-text, #fff);
  font-size: 0.8rem;
  font-weight: 700;
}
.wl-brand-icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--brand-text, #1f2937) 7%, var(--brand-bg, #fff));
  color: var(--brand-text, #1f2937);
}

/* ── Content sections ─────────────────────────────────────────────── */
.wl-cta-banner {
  border: 1px solid var(--brand-border, transparent);
  box-shadow: var(--brand-shadow, none);
  position: relative;
  margin-top: 40px;
  aspect-ratio: 16 / 5.5;
  min-height: 200px;
  border-radius: calc(var(--brand-radius, 14px) * 1.5);
  overflow: hidden;
}
.wl-cta-banner img,
.wl-cta-tile-media img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.wl-cta-shade {
  position: absolute;
  inset: 0;
  /* Same wash as the homepage hero banner: near-clear at the top, deepening to the bottom
     (6% → 28% → 72%). Colour/strength/darken/direction come from the theme's Image overlay
     settings; the default colour is a neutral dark blue-grey, never the brand primary. */
  background: linear-gradient(
    var(--brand-overlay-direction, to bottom),
    color-mix(
        in srgb,
        var(--brand-overlay-colour, #1e293b) calc(6% * var(--brand-overlay-strength, 1)),
        transparent
      )
      0%,
    color-mix(
        in srgb,
        var(--brand-overlay-colour, #1e293b) calc(28% * var(--brand-overlay-strength, 1)),
        transparent
      )
      45%,
    color-mix(
        in srgb,
        var(--brand-overlay-colour, #1e293b) calc(72% * var(--brand-overlay-darken, 1)),
        transparent
      )
      100%
  );
}
.wl-cta-button {
  position: absolute;
  right: 28px;
  bottom: 24px;
  padding: 14px 32px;
  border-radius: calc(var(--brand-radius, 14px) * 2.5);
  background: var(--brand-button, #111827);
  color: var(--brand-button-text, #fff);
  font-weight: 700;
  font-size: 1.05rem;
  text-decoration: none;
  box-shadow: 0 8px 24px -8px rgba(0, 0, 0, 0.5);
}
.wl-cta-button:hover {
  filter: brightness(1.08);
}
.wl-cta-button.small {
  right: 16px;
  bottom: 16px;
  padding: 10px 22px;
  font-size: 0.95rem;
}
.wl-section-title {
  margin-top: 40px;
  padding: 8px 0;
  text-align: center;
  color: var(--brand-text, #1f2937);
}
.wl-section-title h2 {
  margin: 0 0 8px;
  font-family: var(--brand-heading-font, var(--brand-font, inherit));
  font-size: 1.4rem;
  font-weight: 700;
}
.wl-section-title p {
  margin: 0 auto;
  max-width: 520px;
  font-size: 1.05rem;
  line-height: 1.5;
}
.wl-cta-tiles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  margin-top: 24px;
}
.wl-cta-tile {
  border: 1px solid var(--brand-border, transparent);
  box-shadow: var(--brand-shadow, none);
  border-radius: calc(var(--brand-radius, 14px) * 1.2);
  overflow: hidden;
  background: color-mix(in srgb, var(--brand-text, #1f2937) 4%, var(--brand-bg, #fff));
}
.wl-cta-tile-media {
  position: relative;
  aspect-ratio: 4 / 3;
}
.wl-cta-tile-copy {
  margin: 0;
  padding: 16px 18px 20px;
  font-size: 0.9rem;
  line-height: 1.5;
  color: var(--brand-text, #1f2937);
}

/* Video CTA: centred, narrower than the page, button beneath. */
.wl-video-cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
  margin-top: 24px;
}
.wl-video-frame {
  border: 1px solid var(--brand-border, transparent);
  box-shadow: var(--brand-shadow, none);
  position: relative;
  width: 100%;
  max-width: 820px;
  aspect-ratio: 16 / 9;
  border-radius: calc(var(--brand-radius, 14px) * 1.2);
  overflow: hidden;
  background: color-mix(in srgb, var(--brand-primary, #6b7280) 20%, #000);
}
.wl-video-frame iframe,
.wl-video-frame video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  object-fit: cover;
}
.wl-video-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 16px 40px;
  border-radius: calc(var(--brand-radius, 14px) * 2.5);
  background: var(--brand-button, #111827);
  color: var(--brand-button-text, #fff);
  font-weight: 700;
  font-size: 1.1rem;
  text-decoration: none;
}
.wl-video-button:hover {
  filter: brightness(1.08);
}
/* Offer terms: a soft panel of paragraphs at the foot of the page. */
.wl-offer-terms {
  border: 1px solid var(--brand-border, transparent);
  box-shadow: var(--brand-shadow, none);
  margin-top: 40px;
  padding: 36px 40px;
  border-radius: calc(var(--brand-radius, 14px) * 1.2);
  background: color-mix(in srgb, var(--brand-text, #1f2937) 4%, var(--brand-bg, #fff));
  color: var(--brand-text, #1f2937);
}
.wl-offer-terms h2 {
  margin: 0 0 18px;
  font-family: var(--brand-heading-font, var(--brand-font, inherit));
  font-size: 1.3rem;
  font-weight: 700;
}
.wl-offer-terms p {
  margin: 0;
  font-size: 1rem;
  line-height: 1.6;
}
.wl-offer-terms p + p {
  margin-top: 18px;
}

@media (max-width: 720px) {
  .wl-offer-terms {
    padding: 24px 20px;
  }
  .wl-video-button {
    width: 100%;
  }
  .wl-cta-tiles {
    grid-template-columns: 1fr;
  }
  .wl-cta-button {
    right: 16px;
    bottom: 16px;
    padding: 10px 22px;
  }
  /* The hero's desktop min-height made it near-square on phones, so the 16:5 banner was
     cropped hard at the sides. Let the aspect ratio win on small screens. */
  .wl-brand-page {
    padding: 12px 0 40px;
  }
  .wl-brand-hero {
    aspect-ratio: 16 / 9;
    min-height: 0;
  }
  .wl-brand-body {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-top: 20px;
    padding: 0;
  }
  .wl-brand-copy h1 {
    font-size: 1.3rem;
    margin-bottom: 8px;
  }
  .wl-brand-description {
    font-size: 0.95rem;
  }
  .wl-brand-cta {
    margin-top: 18px;
    padding: 13px 24px;
    font-size: 1rem;
  }
  .wl-brand-card {
    padding: 16px 18px;
  }
  .wl-brand-card h3 {
    margin-bottom: 12px;
  }
  .wl-brand-steps li + li,
  .wl-brand-details li + li {
    margin-top: 10px;
  }
  .wl-cta-banner {
    margin-top: 24px;
    aspect-ratio: 16 / 9;
    min-height: 0;
  }
  .wl-cta-tiles {
    margin-top: 16px;
    gap: 16px;
  }
  .wl-brand-logo {
    left: 16px;
    bottom: 16px;
    width: 64px;
    height: 64px;
  }
  .wl-brand-pill {
    right: 16px;
    bottom: 16px;
    padding: 10px 18px;
  }
  .wl-brand-cta {
    width: 100%;
  }
}
</style>
